"use client";

import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useReactToPrint } from "react-to-print";
import {
  useCustomerBundle,
  useCustomerContactNote,
  useCustomerPayment,
} from "@/hooks/api/useCustomers";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { appAbsoluteUrl } from "@/lib/app-base";
import { cn } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";

const tierStyle: Record<string, string> = {
  bronze: "bg-amber-100 text-amber-900 border-amber-300",
  silver: "bg-slate-200 text-slate-900 border-slate-400",
  gold: "bg-yellow-200 text-yellow-950 border-yellow-500",
  platinum: "bg-violet-200 text-violet-950 border-violet-500",
};

export default function CustomerProfilePage() {
  const params = useParams();
  const id = Number(params.id);
  const { data, isLoading, isError, refetch } = useCustomerBundle(id);
  const pay = useCustomerPayment(id);
  const noteMut = useCustomerContactNote(id);
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: printRef });

  const [payAmt, setPayAmt] = useState("");
  const [note, setNote] = useState("");

  const qrValue = useMemo(() => {
    if (typeof window === "undefined" || !id) {
      return `customer:${id}`;
    }
    return appAbsoluteUrl(`customers/${id}`);
  }, [id]);

  const salesTimeline = useMemo(() => {
    if (!data?.ledger) {
      return [];
    }
    return data.ledger
      .filter((e) => e.type === "sale")
      .slice()
      .reverse()
      .slice(0, 15);
  }, [data?.ledger]);

  if (!Number.isFinite(id) || id <= 0) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">Invalid customer id.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Loading profile…</div>
    );
  }

  if (isError || !data) {
    return (
      <div className="p-6 space-y-2">
        <p className="text-sm text-destructive">Could not load customer.</p>
        <Link
          to="/customers"
          className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
        >
          Back
        </Link>
      </div>
    );
  }

  const { customer, ledger, loyalty, contacts } = data;
  const tier = customer.loyalty_tier ?? "bronze";

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/customers"
          className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "gap-2")}
        >
          <ArrowLeft className="h-4 w-4" />
          Customers
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">{customer.name}</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,340px)_1fr]">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                <span className="text-muted-foreground">Phone</span>{" "}
                {customer.phone}
              </p>
              {customer.email ? (
                <p>
                  <span className="text-muted-foreground">Email</span>{" "}
                  {customer.email}
                </p>
              ) : null}
              {customer.gstin ? (
                <p>
                  <span className="text-muted-foreground">GSTIN</span>{" "}
                  {customer.gstin}
                </p>
              ) : null}
              <p>
                <span className="text-muted-foreground">Outstanding</span>{" "}
                <span
                  className={cn(
                    "tabular-nums font-medium",
                    Number(customer.outstanding_balance) > 0.01 &&
                      "text-amber-700",
                  )}
                >
                  ₹{Number(customer.outstanding_balance ?? 0).toFixed(2)}
                </span>
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-base">Loyalty card</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={() => handlePrint()}>
                Print
              </Button>
            </CardHeader>
            <CardContent>
              <div
                ref={printRef}
                className="mx-auto w-[280px] rounded-xl border-2 border-dashed border-border bg-white p-4 text-center text-slate-900 shadow-sm print:border-solid print:shadow-none"
              >
                <p className="text-xs uppercase tracking-widest text-muted-foreground">
                  invoice.cprawat.com
                </p>
                <p className="mt-1 font-semibold text-lg leading-tight">{customer.name}</p>
                <div
                  className={cn(
                    "mx-auto mt-2 inline-flex rounded-full border px-3 py-0.5 text-xs font-semibold capitalize",
                    tierStyle[tier] ?? tierStyle.bronze,
                  )}
                >
                  {tier}
                </div>
                <div className="mt-3 flex justify-center">
                  <QRCodeSVG value={qrValue} size={120} level="M" includeMargin={false} />
                </div>
                <p className="mt-3 text-2xl font-bold tabular-nums">
                  {customer.loyalty_points ?? 0}{" "}
                  <span className="text-sm font-normal text-muted-foreground">pts</span>
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Record payment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2">
                <Label htmlFor="amt">Amount (₹)</Label>
                <Input
                  id="amt"
                  inputMode="decimal"
                  value={payAmt}
                  onChange={(e) => setPayAmt(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <Button
                type="button"
                disabled={pay.isPending}
                onClick={() => {
                  const n = Number(payAmt);
                  if (!Number.isFinite(n) || n <= 0) {
                    return;
                  }
                  void pay
                    .mutateAsync({ amount: n, payment_mode: "cash" })
                    .then(() => {
                      setPayAmt("");
                      void refetch();
                    });
                }}
              >
                {pay.isPending ? "Saving…" : "Apply to outstanding"}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Loyalty</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <ul className="list-disc pl-5 text-muted-foreground">
                {(loyalty.tier_benefits ?? []).map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
              <div className="overflow-x-auto rounded-md border max-h-[220px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-medium">Type</th>
                      <th className="p-2 text-right font-medium">Pts</th>
                      <th className="p-2 text-left font-medium">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(loyalty.transactions ?? []).map((t) => (
                      <tr key={t.id} className="border-t">
                        <td className="p-2 capitalize">{t.transaction_type}</td>
                        <td className="p-2 text-right tabular-nums">{t.points}</td>
                        <td className="p-2 text-muted-foreground whitespace-nowrap">
                          {t.created_at}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Purchase timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm border-l-2 border-muted pl-4 ml-1">
                {salesTimeline.map((e) => (
                  <li key={`${e.type}-${e.ref_id}`} className="relative">
                    <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary" />
                    <p className="font-medium">{e.ref}</p>
                    <p className="text-muted-foreground text-xs">{e.detail}</p>
                  </li>
                ))}
                {salesTimeline.length === 0 && (
                  <li className="text-muted-foreground">No sales yet.</li>
                )}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Ledger</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-md border max-h-[360px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-medium">Type</th>
                      <th className="p-2 text-left font-medium">Ref</th>
                      <th className="p-2 text-right font-medium">Debit</th>
                      <th className="p-2 text-right font-medium">Credit</th>
                      <th className="p-2 text-right font-medium">Pts</th>
                      <th className="p-2 text-right font-medium">Bal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((e, i) => (
                      <tr key={`${e.type}-${e.ref_id}-${i}`} className="border-t">
                        <td className="p-2 whitespace-nowrap">{e.type}</td>
                        <td className="p-2 max-w-[140px] truncate">{e.ref}</td>
                        <td className="p-2 text-right tabular-nums">
                          {e.debit > 0 ? e.debit.toFixed(2) : "—"}
                        </td>
                        <td className="p-2 text-right tabular-nums">
                          {e.credit > 0 ? e.credit.toFixed(2) : "—"}
                        </td>
                        <td className="p-2 text-right tabular-nums">
                          {e.points != null ? e.points : "—"}
                        </td>
                        <td className="p-2 text-right tabular-nums font-medium">
                          {e.running_balance.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                placeholder="Contact note…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
              />
              <Button
                type="button"
                disabled={noteMut.isPending || !note.trim()}
                onClick={() => {
                  void noteMut.mutateAsync(note.trim()).then(() => {
                    setNote("");
                    void refetch();
                  });
                }}
              >
                {noteMut.isPending ? "Saving…" : "Save note"}
              </Button>
              <ul className="space-y-2 text-sm border-t pt-3">
                {(contacts ?? []).map((c) => (
                  <li key={c.id} className="rounded-md bg-muted/40 p-2">
                    <p className="text-xs text-muted-foreground">{c.contact_date}</p>
                    <p>{c.notes}</p>
                  </li>
                ))}
                {(contacts ?? []).length === 0 && (
                  <li className="text-muted-foreground">No notes yet.</li>
                )}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
